import React from 'react'

import { Section, Eyebrow, TrustLine } from '../../../components/ui'
import { useSelectors } from '../../../store'
import { readMeta } from '../../../lib/page-prices'
import { washCountLabel } from '../../../lib/social-proof'
import { CoverageCheck } from '../../../components/coverage-check'

import './intro.css'

// THE TOP OF THE PAGE. The H1, the promise and the facts line.
//
// WHAT WAS HERE BEFORE: a collage of seven stock photographs tiled at angles
// beside the H1, from assets/images/services_bricks.webp. It was a bought
// picture of somebody else's cars and it was the first thing on a page whose
// whole job is to be believed about what work gets done and what it costs.
//
// AND NOTHING REPLACES IT, which is a decision rather than an omission. The
// real washes in assets/images/gallery-image-*.webp would have been the
// honest substitute, but they are already the whole of the home page's
// filmstrip and recent-work strip, captioned with the same services and the
// same postcodes, and a third copy of the same ten frames one click later is
// decoration. The harder constraint is DESIGN.md §5: no image above the fold
// on a phone, because the H1 has to be the LCP element. Anything put here
// lands at about 430px at 390 x 844, which is inside the first screen. A
// comparison page is allowed to begin with the thing it compares.
//
// So the top of this page is four short elements and then the table. That is
// also what makes it fast: there is no XHR and no image in front of the first
// paint, and the six prices under the heading come from a meta tag the server
// already wrote.

const readReviewRating = () => {
  const rating = readMeta('gwmc:reviews', {})?.rating
  return rating ? String(rating) : null
}

export const Intro = () => {
  // The live wash count from /api/v2/app_config, exactly as the hero reads
  // it. The fallback is the real total since 2016, not a round number.
  // Null while SHOW_SOCIAL_PROOF is off (lib/social-proof.js).
  const { config } = useSelectors()
  const washLabel = washCountLabel(config)

  // The Google score, secondary to the wash count (Robin, 5 Oct), from the
  // gwmc:reviews meta tag. Never the review count.
  const rating = readReviewRating()

  return (
    <Section className="svc-intro">
      <Eyebrow>
        {washLabel ? `${washLabel} cars washed since 2016` : "Valeting London's cars since 2016"}
        {rating && ` · ★ ${rating} on Google`}
      </Eyebrow>

      {/* BOTH WORDS, in a headline a person would say (Robin, 2 Nov 2026:
          "the site needs to be littered with both car wash and valeting vocab").
          It read "Hand car washes and valets, at your door" until then, which
          opened a headline on the method: "dont go so heavy on hand wash tbh". */}
      <h1 className="svc-intro-h1">Car washing and valeting, at your door</h1>

      <p className="svc-intro-promise">
        Clear prices with no surprises, and every wash done by hand by a professional valeter. Booked
        in two minutes and confirmed the moment you pay.
      </p>

      <TrustLine
        className="svc-intro-trust"
        items={['At least five years in the trade', 'Vetted and insured', 'We bring our own water and power']}
      />

      {/* "Do we cover you?" (Robin, 28 Oct 2026), on the first screen. */}
      <CoverageCheck className="svc-intro-coverage" />
    </Section>
  )
}
